var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { ProfileService } from "../../services/profile/profile.service";
import { emailValidator, phoneNumberValidator } from "../../utils/form.util";
import { birthdayToAge, formatProfileQuickInfo } from "../../utils/profile.util";
import { convertLocalDateToDate, formatFullDate } from '../../utils/date.util';
import { getScaledUrl, MediaSize } from "../../utils/media.util";
import { CommonThemeColors } from '../theme';
import { MixpanelService } from "../../services/analytics/mixpanel.service";
import SearchIcon from "@mui/icons-material/Search";
import { InfoOutlined } from "@mui/icons-material";
import { getStatusColorBasedOnGuestStayInfo } from "../../utils/color.util";
import { useReservationGroups } from "../../utils/hooks/reservations/useReservationGroups";
import { useReadOnly } from "../../utils/hooks/users/useReadOnly";
var PROFILES_PER_PAGE = 4;
// Exact rendered height of a single profile row, derived from the app theme:
//   body2: fontSize 12 px, lineHeight 14 px  →  4 lines × 14 = 56 px
//   3 inner gaps (gap: 0.5 → 4 px each)      →  12 px
//   p: 1 outer padding (8 px top + 8 px bot)  →  16 px
//   1 px borderBottom                         →   1 px
//   ─────────────────────────────────────────────────
//   total                                        85 px
// Multiplied by PROFILES_PER_PAGE it produces exactly 340 px — the scroll-viewport height
// that keeps four cards and nothing more visible at any given moment.
var PROFILE_ROW_HEIGHT = 85; // px
// Parses a free-text query into structured API fields using lightweight heuristics.
var parseSmartQuery = function (query) {
    var trimmed = (query === null || query === void 0 ? void 0 : query.trim()) || '';
    if (!trimmed)
        return {};
    // Email — lowercase so it matches however the DB normalises the address
    if (trimmed.includes('@')) {
        return { email: trimmed.toLowerCase() };
    }
    // Explicit international prefix → phone only
    if (/^\+/.test(trimmed)) {
        return { phone: trimmed };
    }
    var words = trimmed.split(/\s+/);
    if (words.length >= 2) {
        // All tokens purely numeric → phone number typed with spaces (e.g. "0660 123 456")
        if (words.every(function (w) { return /^\d+$/.test(w); })) {
            return { phone: trimmed };
        }
        // Two or more words → firstName (first token) + lastName (remainder)
        return { firstName: words[0], lastName: words.slice(1).join(' ') };
    }
    var stripped = trimmed.replace(/[\s\-().]/g, '');
    // Long digit string (6+) → unambiguous phone
    if (/^\d{6,}$/.test(stripped)) {
        return { phone: trimmed };
    }
    // Short digit string → room number OR partial phone prefix; search both so either match wins
    if (/^\d+$/.test(stripped)) {
        return { roomId: trimmed, phone: trimmed };
    }
    // Single alpha word → cast across both name fields; backend OR logic surfaces the right match
    return { firstName: trimmed, lastName: trimmed };
};
var ProfileMatcher = function (props) {
    var _a, _b, _c;
    var t = useTranslation().t;
    // Read-only users may match/view profiles, but not create new ones.
    var readOnly = useReadOnly();
    // Get all reservation groups associated with the profiles inside the current reservation
    var reservationGroups = (_c = (_b = useReservationGroups(((_a = props.reservation) === null || _a === void 0 ? void 0 : _a.profileIds) || [])) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.content;
    // Then find all profile IDs inside those reservation groups
    var allProfileIdsFromReservationGroups = useMemo(function () {
        return (reservationGroups === null || reservationGroups === void 0 ? void 0 : reservationGroups.flatMap(function (rg) { return rg.profileIds || []; })) || [];
    }, [reservationGroups]);
    var _d = useState([]), matchedProfiles = _d[0], setMatchedProfiles = _d[1];
    var _e = useState(false), loading = _e[0], setLoading = _e[1];
    var _f = useState(false), hasMore = _f[0], setHasMore = _f[1];
    var _g = useState(true), showSuggestedGroupProfiles = _g[0], setShowSuggestedGroupProfiles = _g[1];
    var _h = useState(function () {
        if (props.email)
            return props.email;
        if (props.phone)
            return props.phone;
        if (props.firstName && props.lastName)
            return "".concat(props.firstName, " ").concat(props.lastName);
        if (props.firstName)
            return props.firstName;
        if (props.lastName)
            return props.lastName;
        return props.initialSimpleQuery || '';
    }), smartQuery = _h[0], setSmartQuery = _h[1];
    var smartQueryRef = useRef();
    // New-guest creation dialog state
    var _j = useState(false), showCreateGuestDialog = _j[0], setShowCreateGuestDialog = _j[1];
    var _k = useState(''), dialogFirstName = _k[0], setDialogFirstName = _k[1];
    var _l = useState(''), dialogLastName = _l[0], setDialogLastName = _l[1];
    var _m = useState(''), dialogEmail = _m[0], setDialogEmail = _m[1];
    var _o = useState(''), dialogPhone = _o[0], setDialogPhone = _o[1];
    var _p = useState(false), dialogLastNameError = _p[0], setDialogLastNameError = _p[1];
    var pageRef = useRef(1);
    var scrollContainerRef = useRef(null);
    // Sentinel placed at the end of the profile list; observed by IntersectionObserver
    // to trigger the next page load both when content is short (no overflow yet) and
    // when the user scrolls to the bottom.
    var sentinelRef = useRef(null);
    var loadMoreFnRef = useRef(function () { });
    // false = limited to 6 months + stay, true = all profiles in organization
    var _q = useState(false), showFullSearch = _q[0], setShowFullSearch = _q[1];
    var _r = useState(false), hasFullSearchDone = _r[0], setHasFullSearchDone = _r[1];
    var fetchMatches = function (page, limitedSearch, append) {
        if (page === void 0) { page = 1; }
        if (limitedSearch === void 0) { limitedSearch = true; }
        if (append === void 0) { append = false; }
        return __awaiter(void 0, void 0, void 0, function () {
            var offset, parsed, hasQuery, response_1, incoming_1, total_1, firstNameArr, lastNameArr, response, incoming_2, total, error_1;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        offset = (page - 1) * PROFILES_PER_PAGE;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 5, 6, 7]);
                        parsed = parseSmartQuery(smartQuery);
                        hasQuery = Object.keys(parsed).length > 0;
                        if (!!hasQuery) return [3 /*break*/, 3];
                        setShowSuggestedGroupProfiles(true);
                        setLoading(true);
                        return [4 /*yield*/, ProfileService.getAllProfiles({
                                id: allProfileIdsFromReservationGroups,
                                idNot: (_a = props.reservation) === null || _a === void 0 ? void 0 : _a.profileIds,
                                limit: {
                                    offset: offset,
                                    limit: PROFILES_PER_PAGE,
                                    sort: [{ field: "name", direction: "ASC" }]
                                }
                            })];
                    case 2:
                        response_1 = _b.sent();
                        incoming_1 = (response_1 === null || response_1 === void 0 ? void 0 : response_1.content) || [];
                        if (append) {
                            setMatchedProfiles(function (prev) { return __spreadArray(__spreadArray([], prev, true), incoming_1, true); });
                        }
                        else {
                            setMatchedProfiles(incoming_1);
                        }
                        total_1 = Math.ceil((response_1.totalElements || 0) / PROFILES_PER_PAGE);
                        setHasMore(page < total_1);
                        pageRef.current = page;
                        setHasFullSearchDone(!limitedSearch);
                        setShowFullSearch(response_1.nextCursor === null);
                        return [2 /*return*/];
                    case 3:
                        setShowSuggestedGroupProfiles(false);
                        setLoading(true);
                        firstNameArr = parsed.firstName
                            ? (parsed.lastName ? [parsed.firstName, parsed.lastName] : [parsed.firstName])
                            : undefined;
                        lastNameArr = parsed.lastName
                            ? (parsed.firstName ? [parsed.lastName, parsed.firstName] : [parsed.lastName])
                            : undefined;
                        return [4 /*yield*/, ProfileService.fastSearchK({
                                firstName: firstNameArr,
                                lastName: lastNameArr,
                                roomId: parsed.roomId ? [parsed.roomId] : undefined,
                                email: parsed.email ? [parsed.email] : undefined,
                                phone: parsed.phone ? [parsed.phone] : undefined,
                                limit: {
                                    limit: PROFILES_PER_PAGE,
                                    offset: offset
                                }
                            })];
                    case 4:
                        response = _b.sent();
                        incoming_2 = (response === null || response === void 0 ? void 0 : response.content) || [];
                        if (append) {
                            setMatchedProfiles(function (prev) { return __spreadArray(__spreadArray([], prev, true), incoming_2, true); });
                        }
                        else {
                            setMatchedProfiles(incoming_2);
                        }
                        total = Math.ceil((response.totalElements || 0) / PROFILES_PER_PAGE);
                        setHasMore(page < total);
                        pageRef.current = page;
                        setHasFullSearchDone(!limitedSearch);
                        setShowFullSearch(response.nextCursor === null);
                        return [3 /*break*/, 7];
                    case 5:
                        error_1 = _b.sent();
                        console.error("Error fetching profiles:", error_1);
                        return [3 /*break*/, 7];
                    case 6:
                        setLoading(false);
                        return [7 /*endfinally*/];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    // Increased debounce to 700ms to reduce API calls during fast typing
    var notifySearch = useCallback(debounce(function (request) { return request(); }, 700), []);
    // Keep loadMoreFnRef current so the scroll handler always calls the latest version
    useEffect(function () {
        loadMoreFnRef.current = function () {
            if (!hasMore || loading)
                return;
            fetchMatches(pageRef.current + 1, !hasFullSearchDone, true);
        };
    }, [hasMore, loading, hasFullSearchDone, smartQuery]);
    // IntersectionObserver watches a sentinel div at the end of the profile list.
    // Unlike a scroll listener, it fires even when the list content doesn't yet overflow
    // the container (e.g. the initial PROFILES_PER_PAGE batch exactly fills the viewport),
    // so loading continues until the container is genuinely scrollable, then resumes each
    // time the user scrolls to the bottom.
    useEffect(function () {
        var scrollEl = scrollContainerRef.current;
        var sentinel = sentinelRef.current;
        if (!scrollEl || !sentinel)
            return;
        var observer = new IntersectionObserver(function (_a) {
            var entry = _a[0];
            if (entry.isIntersecting) {
                loadMoreFnRef.current();
            }
        }, { root: scrollEl, threshold: 0 });
        observer.observe(sentinel);
        return function () { return observer.disconnect(); };
    }, [matchedProfiles.length]);
    var handleCreateNew = function () {
        var parsed = parseSmartQuery(smartQuery);
        setDialogFirstName(parsed.firstName || '');
        setDialogLastName(parsed.lastName || '');
        setDialogEmail(parsed.email || '');
        setDialogPhone(parsed.phone || '');
        setDialogLastNameError(false);
        setShowCreateGuestDialog(true);
    };
    var handleDialogClose = function () {
        setShowCreateGuestDialog(false);
        setDialogLastNameError(false);
    };
    var handleDialogSubmit = function () { return __awaiter(void 0, void 0, void 0, function () {
        var trimmedEmail, trimmedPhone, hasEmail, hasPhone, error_2;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!dialogLastName.trim()) {
                        setDialogLastNameError(true);
                        return [2 /*return*/];
                    }
                    trimmedEmail = dialogEmail.trim();
                    trimmedPhone = dialogPhone.trim();
                    hasEmail = !!trimmedEmail && !emailValidator(trimmedEmail);
                    hasPhone = !!trimmedPhone && !phoneNumberValidator(trimmedPhone);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, ((_a = props.onCreateNew) === null || _a === void 0 ? void 0 : _a.call(props, {
                            firstName: dialogFirstName.trim() || undefined,
                            lastName: dialogLastName.trim(),
                            email: hasEmail ? trimmedEmail : undefined,
                            phone: hasPhone ? trimmedPhone : undefined,
                        }))];
                case 2:
                    _b.sent();
                    MixpanelService.getInstance().track('new guest profile created');
                    MixpanelService.getInstance().increment({ 'guests profiles created': 1 });
                    MixpanelService.getInstance().setPeopleProperties({ 'last guest profile created': new Date().toISOString() });
                    setShowCreateGuestDialog(false);
                    return [4 /*yield*/, fetchMatches()];
                case 3:
                    _b.sent();
                    return [3 /*break*/, 5];
                case 4:
                    error_2 = _b.sent();
                    console.error('Error creating profile:', error_2);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        var _a;
        // Reset accumulated state for a fresh search
        pageRef.current = 1;
        setMatchedProfiles([]);
        setHasMore(false);
        notifySearch(function () { return fetchMatches(1, !hasFullSearchDone, false); });
        var parsed = parseSmartQuery(smartQuery);
        (_a = props.onTmpUpdate) === null || _a === void 0 ? void 0 : _a.call(props, parsed.firstName, parsed.lastName, parsed.email, parsed.phone);
    }, [
        props.initialSimpleQuery,
        smartQuery,
        props.date,
        allProfileIdsFromReservationGroups
    ]);
    useEffect(function () {
        try {
            if (smartQueryRef) {
                setTimeout(function () {
                    var _a;
                    (_a = smartQueryRef === null || smartQueryRef === void 0 ? void 0 : smartQueryRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, 100);
            }
        }
        catch (e) {
        }
    }, []);
    var renderMatchedProfile = function (match) {
        var _a, _b, _c, _d;
        var yearsOld;
        if (match === null || match === void 0 ? void 0 : match.birthDay) {
            var years = birthdayToAge(match.birthDay);
            yearsOld = "(".concat(years, " years)");
        }
        var birthdayAndAge = [formatFullDate(convertLocalDateToDate(match.birthDay)), yearsOld].filter(function (x) { return !!x; }).join(' ');
        var _e = getStatusColorBasedOnGuestStayInfo(match), stayStatusColor = _e[0], stayStatusBackgroundColor = _e[1], stayStatusText = _e[2];
        return (_jsxs(Stack, __assign({ direction: 'row', sx: {
                width: '100%',
                borderBottom: "1px solid ".concat(CommonThemeColors.sys.secondary.selected),
                p: 1,
                alignItems: 'center',
                gap: 2
            } }, { children: [_jsx(Avatar, __assign({ src: getScaledUrl(match === null || match === void 0 ? void 0 : match.avatar, MediaSize.SMALL_64, MediaSize.SMALL_64) }, { children: [
                        (_b = (_a = match.firstName) === null || _a === void 0 ? void 0 : _a.substring(0, 1)) === null || _b === void 0 ? void 0 : _b.toUpperCase(),
                        (_d = (_c = match.lastName) === null || _c === void 0 ? void 0 : _c.substring(0, 1)) === null || _d === void 0 ? void 0 : _d.toUpperCase(),
                    ].filter(Boolean).join('') })), _jsx(Box, __assign({ sx: {
                        backgroundColor: stayStatusBackgroundColor,
                        border: "1px solid ".concat(stayStatusColor),
                        padding: 1,
                        borderRadius: 5
                    } }, { children: _jsx(Typography, __assign({ variant: 'body2', sx: {
                            color: stayStatusColor,
                            whiteSpace: 'nowrap'
                        } }, { children: stayStatusText ? t(stayStatusText) : '-' })) })), _jsxs(Stack, __assign({ onClick: function () { return props.onSelected(match); }, sx: { flex: 1, gap: 0.5 } }, { children: [_jsx(Typography, __assign({ variant: 'body2' }, { children: formatProfileQuickInfo(match, {
                                withName: true,
                                withVIP: true,
                                profileNameReversed: true
                            }) })), _jsx(Typography, __assign({ variant: 'body2' }, { children: formatProfileQuickInfo(match, {
                                withRoom: true,
                                withGender: true,
                                withEmail: true,
                                genderValue: t("Gender type ".concat(match.gender))
                            }) })), _jsx(Typography, __assign({ variant: 'body2' }, { children: formatProfileQuickInfo(match, {
                                withPhone: true
                            }) })), _jsx(Typography, __assign({ variant: 'body2', sx: { color: CommonThemeColors.sys.text.secondary } }, { children: birthdayAndAge }))] }), match.id)] }), match.id));
    };
    var renderFullSearch = function () {
        if (!showFullSearch || hasFullSearchDone)
            return;
        return (_jsxs(Stack, __assign({ direction: 'row', gap: 1, alignItems: 'center', sx: {
                mb: 2,
                mt: 2
            } }, { children: [_jsx(Button, __assign({ fullWidth: true, startIcon: _jsx(SearchIcon, {}), variant: "outlined", onClick: function () {
                        pageRef.current = 1;
                        setMatchedProfiles([]);
                        setHasMore(false);
                        fetchMatches(1, false, false);
                    } }, { children: t('Search full guest database') })), _jsx(Tooltip, __assign({ title: t('Search is limited to all active stays from now until 6 months ahead'), arrow: true }, { children: _jsx(InfoOutlined, { fontSize: "small" }) }))] })));
    };
    var renderResults = function () {
        if (loading && !matchedProfiles.length) {
            return _jsx(Box, __assign({ sx: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%"
                } }, { children: _jsx(CircularProgress, {}) }));
        }
        var parsed = parseSmartQuery(smartQuery);
        var hasQuery = Object.keys(parsed).length > 0;
        var hasNoResults = !matchedProfiles.length && hasQuery;
        if (hasNoResults) {
            return (_jsxs(Stack, __assign({ direction: 'column', display: 'flex', flexDirection: "column", width: '100%', gap: 5, alignItems: "center" }, { children: [renderFullSearch(), _jsx(Divider, { sx: {
                            width: '90%',
                            borderColor: CommonThemeColors.black
                        } }), !readOnly && (_jsx(Button, __assign({ color: 'primary', variant: 'contained', onClick: handleCreateNew }, { children: t('Create new guest profile') }))), _jsx(Alert, __assign({ severity: 'info', sx: {
                            mt: 2,
                            mb: 2,
                            ml: 2,
                            mr: 2
                        } }, { children: _jsx(Typography, __assign({ variant: 'body1' }, { children: t('To deliver quick results, Crqlar first searches guests who are currently staying at your hotel, to search for all, click on "Search full guest database"') })) }))] })));
        }
        return (_jsxs(Stack, __assign({ paddingLeft: 1, paddingRight: 1 }, { children: [matchedProfiles.length > 0 ? (_jsxs(Stack, __assign({ alignItems: 'center' }, { children: [showSuggestedGroupProfiles && (_jsx(Typography, __assign({ variant: 'h6' }, { children: t('Suggested from the same reservation group') }))), _jsxs(Box, __assign({ ref: scrollContainerRef, sx: { width: '100%', maxHeight: PROFILES_PER_PAGE * PROFILE_ROW_HEIGHT, overflowY: 'auto', overflowX: 'hidden' } }, { children: [matchedProfiles.map(renderMatchedProfile), _jsx("div", { ref: sentinelRef, style: { height: 1 } })] })), loading && (_jsx(Box, __assign({ display: "flex", justifyContent: "center", py: 1 }, { children: _jsx(CircularProgress, { size: 20 }) })))] }))) : loading ? (_jsx(Box, __assign({ display: "flex", justifyContent: "center", py: 4 }, { children: _jsx(CircularProgress, {}) }))) : (_jsx(Typography, __assign({ textAlign: "center", py: 4, color: "text.secondary" }, { children: t('No results found') }))), !!matchedProfiles.length && renderFullSearch()] })));
    };
    return (_jsxs(Box, __assign({ display: 'flex', width: '100%', justifyContent: 'flex-start', flexDirection: 'column', flex: 1 }, { children: [_jsx(Box, __assign({ display: 'flex', width: '100%', flexDirection: 'column', sx: {
                    mb: 1
                } }, { children: _jsx(Stack, { children: _jsxs(Stack, __assign({ gap: 1, width: '100%', p: 2, boxSizing: 'border-box' }, { children: [_jsx(TextField, { inputRef: smartQueryRef, placeholder: t('Search by name, email, phone or room'), onChange: function (e) { return setSmartQuery(e.target.value); }, value: smartQuery, sx: { width: '100%' }, variant: 'outlined', size: 'small', InputProps: {
                                    startAdornment: (_jsx(SearchIcon, { fontSize: "small", sx: { mr: 1, color: 'text.secondary' } }))
                                } }), !!matchedProfiles.length && !readOnly &&
                                _jsx(Box, __assign({ width: '100%' }, { children: _jsx(Button, __assign({ color: 'primary', variant: 'outlined', onClick: handleCreateNew, size: 'small' }, { children: t('Create new guest') })) }))] })) }) })), _jsx(Stack, __assign({ width: '100%', minHeight: PROFILES_PER_PAGE * PROFILE_ROW_HEIGHT }, { children: renderResults() })), _jsxs(Dialog, __assign({ open: showCreateGuestDialog, onClose: handleDialogClose, fullWidth: true, maxWidth: "sm" }, { children: [_jsx(DialogTitle, { children: t('Create new guest profile') }), _jsx(DialogContent, { children: _jsxs(Stack, __assign({ gap: 2, sx: { mt: 1 } }, { children: [_jsx(TextField, { label: t('First name'), value: dialogFirstName, onChange: function (e) { return setDialogFirstName(e.target.value); }, fullWidth: true, size: "small" }), _jsx(TextField, { label: t('Last name'), value: dialogLastName, onChange: function (e) {
                                        setDialogLastName(e.target.value);
                                        if (e.target.value.trim())
                                            setDialogLastNameError(false);
                                    }, fullWidth: true, size: "small", required: true, error: dialogLastNameError, helperText: dialogLastNameError ? t('Last name is required') : undefined }), _jsx(TextField, { label: t('Email'), value: dialogEmail, onChange: function (e) { return setDialogEmail(e.target.value); }, fullWidth: true, size: "small", type: "email" }), _jsx(TextField, { label: t('Phone number'), value: dialogPhone, onChange: function (e) { return setDialogPhone(e.target.value); }, fullWidth: true, size: "small" })] })) }), _jsxs(DialogActions, { children: [_jsx(Button, __assign({ onClick: handleDialogClose }, { children: t('Cancel') })), _jsx(Button, __assign({ variant: "contained", onClick: handleDialogSubmit }, { children: t('Create profile') }))] })] }))] })));
};
export default ProfileMatcher;
